import { Link } from "react-router-dom";
import { Broadcast, Calendar, Eye, SealCheck, Play } from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { liveShows } from "@/data/mockData";

export default function Live() {
  const lives = liveShows.filter((s) => s.status === "live");
  const sched = liveShows.filter((s) => s.status === "scheduled");
  const featured = lives[0];

  return (
    <Layout wide>
      <div data-testid="live-page">
        {/* Hero featured live */}
        {featured ? (
          <Link
            to={`/live/${featured.id}`}
            data-testid="featured-live"
            className="relative block aspect-[16/9] md:aspect-[21/9] overflow-hidden border-b border-[#27272A] group"
          >
            <img
              src={featured.cover}
              alt={featured.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

            <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
              <div>
                <div className="font-mono text-[10px] text-white tracking-[0.3em] mb-2">
                  LIVE · ARTE · AHORA
                </div>
                <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter uppercase leading-[0.9] max-w-3xl">
                  Transmitiendo
                  <br />
                  <span className="text-[#FF3E00]">en vivo</span>.
                </h1>
              </div>
              <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] bg-[#FF0033] text-white px-3 py-2">
                <span className="h-2 w-2 bg-white live-dot" />
                LIVE · {featured.viewers.toLocaleString("es")} VIENDO
              </span>
            </div>

            <div className="absolute bottom-0 inset-x-0 p-5 md:p-8 flex items-end justify-between gap-6 flex-wrap">
              <div className="flex items-center gap-4 min-w-0">
                <img
                  src={featured.artist.avatar}
                  alt={featured.artist.name}
                  className="h-14 w-14 md:h-16 md:w-16 object-cover border border-[#F4F4F5]"
                />
                <div className="min-w-0">
                  <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.22em]">
                    {featured.discipline}
                  </div>
                  <div className="font-display font-black text-xl md:text-3xl tracking-tight flex items-center gap-2">
                    {featured.artist.name}
                    {featured.artist.verified ? (
                      <SealCheck size={18} weight="fill" className="text-[#FF3E00]" />
                    ) : null}
                  </div>
                  <div className="font-mono text-[11px] text-[#A1A1AA] tracking-wider truncate">
                    {featured.title} · {featured.venue}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="join-live-btn"
                  className="btn-brutal bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold px-6 py-4 inline-flex items-center gap-2"
                >
                  <Play size={14} weight="fill" /> Entrar al stream
                </button>
              </div>
            </div>
          </Link>
        ) : null}

        {/* LIVE NOW grid */}
        <section className="p-4 md:p-6">
          <div className="flex items-center gap-3 mb-4">
            <Broadcast size={18} className="text-[#FF0033]" weight="fill" />
            <h2 className="font-display font-black uppercase tracking-tight text-xl">
              Transmitiendo ahora
            </h2>
            <span className="ml-auto font-mono text-[10px] text-[#A1A1AA] tracking-wider">
              {lives.length} EN VIVO
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {lives.map((s) => (
              <Link
                key={s.id}
                to={`/live/${s.id}`}
                data-testid={`live-card-${s.id}`}
                className="group block border border-[#27272A] hover:border-[#FF3E00] bg-[#0a0a0a] transition-colors"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={s.cover}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] bg-[#FF0033] text-white px-2 py-1">
                      <span className="h-1.5 w-1.5 bg-white live-dot" />
                      LIVE
                    </span>
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] tracking-wider bg-black/70 backdrop-blur-sm px-2 py-1 text-white">
                      <Eye size={11} weight="fill" />
                      {s.viewers.toLocaleString("es")}
                    </span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="h-14 w-14 bg-[#FF3E00] text-white flex items-center justify-center">
                      <Play size={20} weight="fill" />
                    </div>
                  </div>
                </div>
                <div className="p-4 border-t border-[#27272A]">
                  <div className="font-mono text-[10px] text-[#FF3E00] tracking-wider">
                    {s.discipline} · {s.started.toUpperCase()}
                  </div>
                  <h3 className="font-display font-bold text-[15px] tracking-tight mt-1 line-clamp-2">
                    {s.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-3">
                    <img
                      src={s.artist.avatar}
                      alt={s.artist.name}
                      className="h-6 w-6 object-cover border border-[#27272A]"
                    />
                    <span className="font-mono text-[11px] tracking-wider truncate">
                      @{s.artist.handle}
                    </span>
                    {s.artist.verified ? (
                      <SealCheck size={11} weight="fill" className="text-[#FF3E00]" />
                    ) : null}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Agenda */}
        <section className="p-4 md:p-6 border-t border-[#27272A]">
          <div className="flex items-center gap-3 mb-4">
            <Calendar size={18} className="text-[#FF3E00]" weight="fill" />
            <h2 className="font-display font-black uppercase tracking-tight text-xl">
              Agenda · próximos lives
            </h2>
          </div>

          <ul>
            {sched.map((s) => (
              <li
                key={s.id}
                className="grid grid-cols-[auto_1fr_auto] md:grid-cols-[120px_auto_1fr_auto] gap-4 items-center py-4 border-b border-[#27272A]"
              >
                <div className="hidden md:block font-mono text-[11px] text-[#FF3E00] tracking-[0.22em] uppercase">
                  {s.when}
                </div>
                <img
                  src={s.artist.avatar}
                  alt={s.artist.name}
                  className="h-12 w-12 object-cover border border-[#27272A]"
                />
                <div className="min-w-0">
                  <div className="md:hidden font-mono text-[10px] text-[#FF3E00] tracking-[0.22em] uppercase mb-1">
                    {s.when}
                  </div>
                  <h3 className="font-display font-bold tracking-tight truncate">
                    {s.title}
                  </h3>
                  <div className="font-mono text-[11px] text-[#A1A1AA] tracking-wider truncate">
                    {s.artist.name} · {s.venue} · {s.discipline}
                  </div>
                </div>
                <button
                  data-testid={`remind-${s.id}`}
                  className="btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] border border-[#F4F4F5] hover:bg-[#FF3E00] hover:border-[#FF3E00] hover:text-white px-3 py-2 whitespace-nowrap"
                >
                  + Avísame
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Layout>
  );
}
